import { memo, type ReactNode, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

import { UploadContext } from '@air/component-upload-dropzone';
import { PlusIcon, UploadIcon as UploadFilesIcon, FolderUpIcon as UploadFoldersIcon } from '@air/design-icons';
import {
  ContentCollectionView,
  contentCollectionHasClipsSelector,
  contentCollectionViewSelector,
} from '@air/feature-content-collection/redux';
import { Button, type ButtonProps } from '@air/primitive-button';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

export type ContentCollectionUploadMoreButtonProps = Partial<ButtonProps>;

export const ContentCollectionUploadMoreButton = memo(
  ({ className, ...restOfProps }: ContentCollectionUploadMoreButtonProps) => {
    const { isAboveMediumScreen } = useBreakpointsContext();
    const hasClips = useSelector(contentCollectionHasClipsSelector);
    const view = useSelector(contentCollectionViewSelector);

    const { openUploader } = useContext(UploadContext);

    const openFilesUploader = useCallback(() => {
      openUploader({ type: 'files' });
    }, [openUploader]);

    const openFoldersUploader = useCallback(() => {
      openUploader({ type: 'folders' });
    }, [openUploader]);

    const renderUploadMoreButton = useCallback(
      (trigger: ReactNode) => (
        <DropdownMenu trigger={trigger} align="center">
          {renderDropdownItems({
            options: [
              {
                id: 'upload-files',
                label: 'File upload',
                onSelect: openFilesUploader,
                type: 'item',
                prefix: <UploadFilesIcon size="sm" />,
              },
              {
                id: 'upload-folders',
                label: 'Folder upload',
                onSelect: openFoldersUploader,
                type: 'item',
                prefix: <UploadFoldersIcon size="sm" />,
              },
            ],
          })}
        </DropdownMenu>
      ),
      [openFilesUploader, openFoldersUploader],
    );

    const isLoading = view === ContentCollectionView.Manage && !hasClips;

    if (isAboveMediumScreen) {
      return renderUploadMoreButton(
        <Button
          appearance="outline"
          color="gray"
          size="large"
          prefix={<PlusIcon size="sm" />}
          data-testid="CONTENT_COLLECTION_UPLOAD_MORE"
          className={cn('rounded-8 text-gray-12', className)}
          isLoading={isLoading}
          {...restOfProps}
        >
          {restOfProps.children || 'Add more'}
        </Button>,
      );
    }

    return (
      <Button
        appearance="outline"
        color="gray"
        size="large"
        prefix={<PlusIcon size="sm" />}
        data-testid="CONTENT_COLLECTION_UPLOAD_MORE"
        className={cn('rounded-8 text-gray-12', className)}
        onClick={openFilesUploader}
        isLoading={isLoading}
        {...restOfProps}
      >
        {restOfProps.children || 'Add more'}
      </Button>
    );
  },
);

ContentCollectionUploadMoreButton.displayName = 'ContentCollectionUploadMoreButton';
